import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import {
    getHasOneTimeAmountToDisplay,
    getHasRecurringAmountToDisplay,
    getHasEnabledActiveStandardRestrictions,
} from "@qgiv/donation-form";
import { selectAllDonationSettings } from "../../../redux/slices/donationSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectGiftDetails } from "../../../redux/slices/donationDetailsSlice";
import { selectRestrictions } from "../../../redux/slices/restrictionSettingsSlice";
import { selectCurrentDisplay } from "../../../redux/slices/configSettingsSlice";
import { selectHasActiveFulfillment } from "../../../redux/slices/promiseTransactionSlice";
import {
    selectCurrentPage,
    selectIsSinglePageForm,
} from "../../../redux/slices/appSettingsSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllSystemFields } from "../../../redux/slices/fieldsSlice";
import { getMatchingGiftNoticeFlag } from "./matchingGiftNoticeHelpers";
import DonatedGiftControl from "./DonatedGiftControl";

const ConnectedDonatedGiftControl = ({ controlId }) => {
    const {
        ENUMS: { EntityType },
    } = constants;
    const hasActiveFulfillment = useSelector(selectHasActiveFulfillment);

    const {
        activeAmounts,
        activePlans,
        givingPlanSystemType,
        pledgeActive,
        restrictionGivingType,
        enableMatching,
    } = useSelector(selectAllDonationSettings);
    const systemFields = useSelector(selectAllSystemFields);
    const matchingGiftSystemField = systemFields.find(
        (field) => field.type === EntityType.SYSTEM_FIELD_GROUP_MATCHING_GIFTS,
    );
    const { donationActive, enableRecur } = useSelector(selectAllFormSettings);
    const hasOneTimeDonationAmountToDisplay =
        getHasOneTimeAmountToDisplay(activeAmounts);
    const { hasSelectedRecurringDonation } = useSelector(selectGiftDetails);
    const restrictions = useSelector(selectRestrictions);
    const currentDisplay = useSelector(selectCurrentDisplay);
    const isSinglePageForm = useSelector(selectIsSinglePageForm);

    const hasRecurringDonationAmountToDisplay = getHasRecurringAmountToDisplay(
        activeAmounts,
        activePlans,
        pledgeActive,
    );

    const shouldDisplayGivingPlansContent =
        !hasActiveFulfillment &&
        activePlans?.length > 0 &&
        pledgeActive &&
        hasSelectedRecurringDonation;

    const shouldDisplaySustainingGiftsContent =
        !hasActiveFulfillment &&
        hasSelectedRecurringDonation &&
        !shouldDisplayGivingPlansContent;

    const shouldDisplayOneTimeGiftContent =
        !hasActiveFulfillment && !hasSelectedRecurringDonation;

    const showDonationFrequencyTabs =
        donationActive &&
        enableRecur &&
        hasOneTimeDonationAmountToDisplay &&
        hasRecurringDonationAmountToDisplay &&
        !hasActiveFulfillment;

    const hasEnabledActiveStandardRestrictions =
        getHasEnabledActiveStandardRestrictions({
            restrictions,
            hasActiveFulfillment,
            currentDisplay,
            restrictionGivingType,
        });

    const enableStandardRestrictions = // (!hasSelectedRecurringDonation && pledgeActive) &&
        hasEnabledActiveStandardRestrictions;

    const shouldDisplayRestrictions =
        enableStandardRestrictions && !shouldDisplayGivingPlansContent;

    const currentPage = useSelector(selectCurrentPage);
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(state, controlId, currentPage),
    );
    const {
        options: { displayMatchingGiftNotice = false },
    } = control;

    const shouldDisplayMatchingGiftNotice = getMatchingGiftNoticeFlag({
        pledgeActive,
        activePlans,
        givingPlanSystemType,
        hasSelectedRecurringDonation,
        isSinglePageForm,
        matchingGiftSystemField,
        currentDisplay,
        hasActiveFulfillment,
        enableMatching,
        displayMatchingGiftNotice,
    });

    const donatedGiftControlProps = {
        hasActiveFulfillment,
        enableStandardRestrictions,
        showDonationFrequencyTabs,
        hasSelectedRecurringDonation,
        controlId,
        shouldDisplayGivingPlansContent,
        shouldDisplayMatchingGiftNotice,
        shouldDisplaySustainingGiftsContent,
        shouldDisplayOneTimeGiftContent,
        shouldDisplayRestrictions,
    };

    return <DonatedGiftControl {...donatedGiftControlProps} />;
};

ConnectedDonatedGiftControl.propTypes = {
    controlId: PropTypes.number.isRequired,
};

export default ConnectedDonatedGiftControl;
